import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import ProgressBar from './ProgressBar';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Users,
    DollarSign,
    CreditCard,
    CheckCircle2,
    ChevronLeft
} from 'lucide-react';

function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!location.state || !location.state.bookingDetails) {
        navigate('/booking');
        return null;
    }

    const { room, guests, bookingDetails } = location.state;

    // Helper function to determine season
    const isSummerSeason = (date) => {
        const checkDate = new Date(date);
        const month = checkDate.getMonth();
        return month >= 3 && month <= 8; // April (3) to September (8)
    };

    const currentSeason = room.season || (isSummerSeason(room.checkIn) ? 'Summer' : 'Winter');

    const handleCheckout = () => {
        setLoading(true);
        const paymentMethod = 'Advance Payment / Bank Transfer';
        sendWhatsAppMessage(room, guests, bookingDetails, paymentMethod);

        setTimeout(() => {
            navigate('/confirmation', {
                state: {
                    room,
                    guests,
                    bookingDetails,
                    paymentMethod,
                }
            });
        }, 1000);
    };

    const sendWhatsAppMessage = (room, guests, bookingDetails, paymentMethod) => {
        const bookingId = Math.floor(Math.random() * 100000000);

        const message = `*New Booking Request*\n\n` +
            `*Booking ID:* ${bookingId}\n` +
            `*Payment Method:* ${paymentMethod}\n\n` +
            `*Guest Details:*\n` +
            `Name: ${bookingDetails.firstName} ${bookingDetails.lastName}\n` +
            `Email: ${bookingDetails.email}\n` +
            `Phone: ${bookingDetails.phone}\n` +
            `Address: ${bookingDetails.address}, ${bookingDetails.city}, ${bookingDetails.country} ${bookingDetails.zip}\n\n` +
            `*Booking Details:*\n` +
            `Room: ${room.name}\n` +
            `Check In: ${new Date(room.checkIn).toLocaleDateString()}\n` +
            `Check Out: ${new Date(room.checkOut).toLocaleDateString()}\n` +
            `Guests: ${guests} Adult(s) + 0 Children\n` +
            `Total Price: €${room.totalPrice.toFixed(2)}`;

        const whatsappUrl = `https://wa.me/94766111678?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">

            <ProgressBar currentStep="checkout" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="space-y-6">
                    {/* Booking Details Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700"
                    >
                        <div className="border-b border-gray-200 pb-4 mb-6">
                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 text-center flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-7 h-7 text-green-700" />
                                Booking Summary
                            </h2>
                        </div>

                        <div className="space-y-3">
                            {/* Name */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <User className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">First Name:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.firstName}
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <User className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Last Name:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.lastName}
                                </span>
                            </div>

                            {/* Email */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Mail className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Email:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base break-all pl-6 sm:pl-0">
                                    {bookingDetails.email}
                                </span>
                            </div>

                            {/* Phone */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Phone className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Phone:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.phone}
                                </span>
                            </div>

                            {/* Address */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Address:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base break-words pl-6 sm:pl-0 sm:text-right">
                                    {bookingDetails.address}
                                </span>
                            </div>

                            {/* City */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">City:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.city}
                                </span>
                            </div>

                            {/* Country */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Country:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.country}
                                </span>
                            </div>

                            {/* ZIP */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">ZIP Code:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.zip}
                                </span>
                            </div>

                            {/* Check In */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Calendar className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Check In:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {new Date(room.checkIn).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Check Out */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Calendar className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Check Out:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {new Date(room.checkOut).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Guests */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Users className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Guests:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {guests} Adult{guests > 1 ? 's' : ''} + 0 Children
                                </span>
                            </div>

                            {/* Seasonal Rates Information */}
                            <div className="mt-4 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-sm font-medium text-gray-700 mb-3">Seasonal Rates</p>

                                {/* Summer Rates */}
                                <div className={`mb-2 p-2.5 rounded-lg border ${currentSeason === 'Summer' ? 'bg-amber-100/50 border-amber-300' : 'bg-white border-gray-200'
                                    }`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-amber-700">🌞 Summer Season</span>
                                        {currentSeason === 'Summer' && (
                                            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Active</span>
                                        )}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Single:</span>
                                            <span className={`font-semibold ${currentSeason === 'Summer' ? 'text-green-700' : 'text-gray-600'
                                                }`}>€105/night</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Double:</span>
                                            <span className={`font-semibold ${currentSeason === 'Summer' ? 'text-green-700' : 'text-gray-600'
                                                }`}>€180/night</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">April - September</p>
                                </div>

                                {/* Winter Rates */}
                                <div className={`p-2.5 rounded-lg border ${currentSeason === 'Winter' ? 'bg-blue-100/50 border-blue-300' : 'bg-white border-gray-200'
                                    }`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-blue-700">❄️ Winter Season</span>
                                        {currentSeason === 'Winter' && (
                                            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Active</span>
                                        )}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Single:</span>
                                            <span className={`font-semibold ${currentSeason === 'Winter' ? 'text-green-700' : 'text-gray-600'
                                                }`}>€145/night</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Double:</span>
                                            <span className={`font-semibold ${currentSeason === 'Winter' ? 'text-green-700' : 'text-gray-600'
                                                }`}>€250/night</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">October - March</p>
                                </div>
                            </div>

                            {/* Total Price */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 bg-gradient-to-r from-amber-50 to-green-50 rounded-lg px-4 mt-4 border border-amber-200">
                                <div className="flex flex-col gap-0.5 text-green-900 mb-1 sm:mb-0">
                                    <div className="flex items-center gap-2">
                                        <DollarSign className="w-5 h-5 text-green-700" />
                                        <span className="font-bold text-base md:text-lg">Total Price:</span>
                                    </div>
                                    <span className="text-xs text-gray-600 pl-7">
                                        {guests === 1 ? 'Single' : 'Double'} Occupancy • {currentSeason} Season
                                    </span>
                                </div>
                                <span className="text-green-700 font-bold text-lg md:text-xl pl-6 sm:pl-0">
                                    €{room.totalPrice.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Payment Options Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700"
                    >
                        <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 flex items-center justify-center gap-2">
                            <CreditCard className="w-7 h-7 text-green-700" />
                            Payment Details
                        </h2>

                        {/* Account Details */}
                        <div className="mb-6 space-y-4">
                            <div className="rounded-xl border border-green-100 bg-green-50/60 p-4">
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Account Name:</span> P. M. K. Abeygunawardena
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Bank Name:</span> Hatton National Bank
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Account Number:</span> 034810210869 (Euro Account)
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">SWIFT Code:</span> HBLILKLX001
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Currency:</span> Sri Lankan Rupee (LKR)
                                </p>
                            </div>

                            <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-4">
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Hotel Name:</span> Mahi Beach Ayurveda Resort - Payagala
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Owner Name:</span> P. M. K. Abeygunawardena
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Address:</span> Mahi Beach Ayurveda, No. 112, Galle Road, Payagala, Sri Lanka
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Contact Number:</span> +94 773036444
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Email:</span> mahibeach23@gmail.com
                                </p>
                                <p className="text-sm md:text-base text-gray-700">
                                    <span className="font-semibold">Additional Contact:</span> +94 777194904
                                </p>
                            </div>

                            <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                                <p className="text-sm md:text-base text-blue-900 font-semibold">
                                    Advance Payment: 10% is required to confirm the booking.
                                </p>
                                <p className="text-sm md:text-base text-blue-800 mt-1">
                                    The remaining balance should be paid after arrival.
                                </p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pt-6 border-t border-gray-200 mt-6">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate(-1)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all duration-300 text-sm md:text-base"
                            >
                                <ChevronLeft className="w-5 h-5" />
                                Go Back
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02, boxShadow: '0 10px 25px rgba(16, 185, 129, 0.2)' }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleCheckout}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-green-400 disabled:to-green-500 text-white font-bold rounded-lg transition-all duration-300 text-sm md:text-base shadow-lg hover:shadow-xl"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-5 h-5" />

                                    </>
                                )}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div >
    );
}

export default Checkout;
